import React from 'react'
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

interface PayPalButtonProps {
  orderData: {
    orderNumber: string;
    customerInfo: any;
    items: any[];
    total: number;
    timestamp: string;
    trackingNumber?: string;
  };
}

const PayPalButton: React.FC<PayPalButtonProps> = ({ orderData }) => {
  const [{ isResolved, isPending, options }] = usePayPalScriptReducer()
  const navigate = useNavigate()

  console.log('PayPal Script State:', { isResolved, isPending, options })

  if (!isResolved) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">PayPal wird geladen...</p>
        <p className="text-xs text-gray-500 mt-1">
          Debug: Resolved={String(isResolved)}, Pending={String(isPending)}
        </p>
        <p className="text-xs text-gray-500">
          Client ID: {options?.clientId || 'NICHT GEFUNDEN'}
        </p>
      </div>
    )
  }

  return (
    <PayPalButtons
      style={{
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal',
        height: 45
      }}
      createOrder={async (_data, _actions) => {
        try {
          console.log('Creating PayPal order via backend for:', orderData.orderNumber)

          // Create order via backend API
          const response = await fetch(`${import.meta.env.VITE_API_URL}/payment/paypal/create-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: orderData.total.toFixed(2),
              currency: 'EUR',
              description: `PhoneMedic Reparatur - ${orderData.orderNumber}`,
              orderId: orderData.orderNumber
            }),
          })

          if (!response.ok) {
            console.error('Backend PayPal order creation failed:', response.status, response.statusText)
            const errorText = await response.text()
            console.error('Error details:', errorText)
            throw new Error(`Backend error: ${response.status}`)
          }

          const paypalOrder = await response.json()
          console.log('Backend PayPal order created:', paypalOrder)

          return paypalOrder.id
        } catch (error) {
          console.error('PayPal createOrder error:', error)
          toast.error('Fehler beim Erstellen der PayPal-Bestellung')
          throw error
        }
      }}
      onApprove={async (data, _actions) => {
        if (!data.orderID) {
          console.error('PayPal onApprove: No orderID found')
          toast.error('PayPal Fehler: Keine Bestellung gefunden')
          return
        }

        try {
          console.log('PayPal onApprove: Capturing payment via backend for orderID:', data.orderID)

          // Capture payment via backend API
          const response = await fetch(`${import.meta.env.VITE_API_URL}/payment/paypal/capture-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              paypalOrderId: data.orderID
            }),
          })

          if (!response.ok) {
            console.error('Backend PayPal capture failed:', response.status, response.statusText)
            const errorText = await response.text()
            console.error('Capture error details:', errorText)
            throw new Error(`Backend capture error: ${response.status}`)
          }

          const orderDetails = await response.json()
          console.log('PayPal payment captured via backend:', orderDetails)
          
          // Update order with payment info
          const completedOrderData = {
            ...orderData,
            paymentId: orderDetails.id,
            paymentStatus: 'completed',
            paymentMethod: 'paypal',
            paymentDetails: orderDetails
          }

          console.log('Sending order to backend:', completedOrderData)

          // Send order to backend for DHL label generation
          try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/orders/create-with-shipping`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(completedOrderData),
            })

            if (response.ok) {
              const result = await response.json()
              console.log('Backend response:', result)
              completedOrderData.trackingNumber = result.data?.trackingNumber
              toast.success('Zahlung erfolgreich! DHL Label wird per E-Mail versendet.')
            } else {
              console.error('Backend order creation failed:', response.status, response.statusText)
              toast.success('Zahlung erfolgreich! DHL Label wird in Kürze versendet.')
            }
          } catch (backendError) {
            console.error('Backend communication error:', backendError)
            toast.success('Zahlung erfolgreich! DHL Label wird in Kürze versendet.')
          }
          
          // Navigate to success page with completed order data
          navigate('/order-confirmation', { 
            state: { orderData: completedOrderData } 
          })
          
        } catch (error) {
          console.error('PayPal capture error:', error)
          toast.error('Fehler bei der Zahlungsabwicklung. Bitte versuchen Sie es erneut.')
        }
      }}
      onError={(error) => {
        console.error('PayPal error:', error)
        
        // Provide more specific error messages
        let errorMessage = 'PayPal Fehler. Bitte versuchen Sie es erneut.'
        const errorString = String(error)
        
        if (errorString.includes('ORDER_NOT_APPROVED')) {
          errorMessage = 'Zahlung nicht genehmigt. Bitte versuchen Sie es erneut.'
        } else if (errorString.includes('INSTRUMENT_DECLINED')) {
          errorMessage = 'Zahlungsmethode abgelehnt. Versuchen Sie eine andere Karte.'
        } else if (typeof error === 'object' && error?.message) {
          console.error('PayPal error details:', error.message)
        }
        
        toast.error(errorMessage)
      }}
      onCancel={() => {
        toast('Zahlung abgebrochen')
      }}
    />
  )
}

export default PayPalButton